<template>
  <section id="section-mypage" class="mypage-section">
    <header class="mypage-section__header">
      <h2>{{ $t('mypage.title') }}</h2>
      <p>{{ $t('mypage.desc') }}</p>
    </header>

    <p v-if="loading" class="mypage-section__status">{{ $t('mypage.loading') }}</p>
    <p v-else-if="loadError" class="mypage-section__status mypage-section__status--error">{{ loadError }}</p>

    <div v-else-if="member" class="mypage-grid">
      <article class="mypage-card mypage-card--profile">
        <h3 class="mypage-card__title">{{ $t('mypage.profileTitle') }}</h3>
        <p class="mypage-profile__name">{{ displayValue(member.loginid) }}</p>

        <dl class="mypage-dl">
          <div class="mypage-dl__row">
            <dt>{{ $t('mypage.loginId') }}</dt>
            <dd>{{ displayValue(member.loginid) }}</dd>
          </div>
          <div class="mypage-dl__row">
            <dt>{{ $t('mypage.memberLevel') }}</dt>
            <dd>{{ levelLabel }}</dd>
          </div>
          <div class="mypage-dl__row">
            <dt>{{ $t('mypage.name') }}</dt>
            <dd>{{ displayValue(member.name) }}</dd>
          </div>
          <div class="mypage-dl__row">
            <dt>{{ $t('mypage.phone') }}</dt>
            <dd>{{ displayValue(member.tel) }}</dd>
          </div>
          <div class="mypage-dl__row">
            <dt>{{ $t('mypage.referralCode') }}</dt>
            <dd>{{ displayValue(member.code, $t('mypage.referralCodeEmpty')) }}</dd>
          </div>
          <div class="mypage-dl__row">
            <dt>{{ $t('mypage.bankHolder') }}</dt>
            <dd>{{ displayValue(member.bank_master) }}</dd>
          </div>
          <div class="mypage-dl__row">
            <dt>{{ $t('mypage.bankName') }}</dt>
            <dd>{{ displayValue(member.bank_name) }}</dd>
          </div>
          <div class="mypage-dl__row">
            <dt>{{ $t('mypage.bankAccount') }}</dt>
            <dd>{{ displayValue(member.bank_account) }}</dd>
          </div>
          <div class="mypage-dl__row">
            <dt>{{ $t('mypage.joinDate') }}</dt>
            <dd>{{ formatDateTime(member.regdate) }}</dd>
          </div>
          <div class="mypage-dl__row">
            <dt>{{ $t('mypage.lastLogin') }}</dt>
            <dd>{{ formatDateTime(member.last_login_date) }}</dd>
          </div>
        </dl>
      </article>

      <article class="mypage-card mypage-card--wallet">
        <h3 class="mypage-card__title">{{ $t('mypage.walletTitle') }}</h3>

        <div class="mypage-balance-grid">
          <div class="mypage-balance-box">
            <span class="mypage-balance-box__label">{{ $t('mypage.balance') }}</span>
            <strong class="mypage-balance-box__value">{{ formatBalance(member.balance) }}</strong>
          </div>
          <div class="mypage-balance-box">
            <span class="mypage-balance-box__label">{{ $t('mypage.bonus') }}</span>
            <strong class="mypage-balance-box__value mypage-balance-box__value--point">{{ formatBalance(member.bonus) }}</strong>
          </div>
        </div>

        <ul class="mypage-wallet-list">
          <li>
            <span>{{ $t('mypage.apiBalance') }}</span>
            <strong>{{ formatBalance(member.api_balance) }}</strong>
          </li>
          <li>
            <span>{{ $t('mypage.point') }}</span>
            <strong>{{ formatPoint(member.point) }}</strong>
          </li>
          <li>
            <span>{{ $t('mypage.accountStatus') }}</span>
            <strong>{{ statusLabel }}</strong>
          </li>
          <li>
            <span>{{ $t('mypage.lastDepositDate') }}</span>
            <strong>{{ formatDateTime(member.last_charge_date) }}</strong>
          </li>
          <li>
            <span>{{ $t('mypage.lastBetDate') }}</span>
            <strong>{{ formatDateTime(member.last_beting_date) }}</strong>
          </li>
        </ul>

        <div class="mypage-actions">
          <button
            v-if="walletEnabled"
            type="button"
            class="mypage-btn"
            @click="$emit('deposit')"
          >{{ $t('mypage.deposit') }}</button>
          <button
            v-if="walletEnabled"
            type="button"
            class="mypage-btn"
            @click="$emit('withdraw')"
          >{{ $t('mypage.withdraw') }}</button>
          <button type="button" class="mypage-btn" @click="openPointConvert">{{ $t('mypage.pointConvert') }}</button>
        </div>

        <div class="mypage-links">
          <div class="mypage-link-item">
            <div>
              <strong>{{ $t('mypage.inquiryTitle') }}</strong>
              <span>{{ $t('mypage.inquiryDesc') }}</span>
            </div>
            <button type="button" class="mypage-btn mypage-btn--ghost" @click="$emit('inquiry')">{{ $t('mypage.goTo') }}</button>
          </div>
          <div class="mypage-link-item">
            <div>
              <strong>{{ $t('mypage.bettingTitle') }}</strong>
              <span>{{ $t('mypage.bettingDesc') }}</span>
            </div>
            <button type="button" class="mypage-btn mypage-btn--ghost" @click="$emit('betting')">{{ $t('mypage.goTo') }}</button>
          </div>
        </div>
      </article>
    </div>

    <BlackGoldPointConvertModal
      :visible="pointConvertModalOpen"
      @close="closePointConvert"
      @success="onPointConvertSuccess"
    />
  </section>
</template>

<script>
import BlackGoldPointConvertModal from '@/components/black-gold/BlackGoldPointConvertModal.vue'
import { AuthService } from '@/services'
import { displayValue, formatDateTime, formatPointAmount } from '@/utils/format'
import { getMemberLevelLabel, getMemberStatusLabel } from '@/utils/member'
import { showToast } from '@/utils/toast'

export default {
  name: 'BlackGoldMyPage',
  components: {
    BlackGoldPointConvertModal
  },
  props: {
    walletEnabled: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      loading: false,
      loadError: '',
      member: null,
      pointConvertModalOpen: false
    }
  },
  computed: {
    levelLabel () {
      return getMemberLevelLabel(this.member)
    },
    statusLabel () {
      return getMemberStatusLabel(this.member && this.member.status)
    }
  },
  mounted () {
    this.loadUserInfo()
  },
  methods: {
    displayValue,
    formatDateTime,
    formatPoint (value) {
      return `${formatPointAmount(value)} P`
    },
    async loadUserInfo () {
      this.loading = true
      this.loadError = ''
      try {
        const res = await AuthService.getUserInfo()
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data) {
          this.member = body.data
        } else {
          this.member = null
          this.loadError = (body && body.message) ? body.message : this.$t('mypage.loadError')
        }
      } catch (err) {
        this.member = null
        this.loadError = this.$t('mypage.loadError')
      } finally {
        this.loading = false
      }
    },
    reload () {
      return this.loadUserInfo()
    },
    openPointConvert () {
      this.pointConvertModalOpen = true
    },
    closePointConvert () {
      this.pointConvertModalOpen = false
    },
    async onPointConvertSuccess () {
      await this.reload()
      this.$emit('balance-updated')
      showToast(this.$t('mypage.pointConvertSuccess'))
    }
  }
}
</script>
